import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { PALETTE as P } from "../tokens";
import { useAuth } from "../context/AuthContext";
import { practitionerLinkApi } from "../api/practitionerLink";
import { localDateKey } from "../utils/screenFollowthrough";
import { consumePendingConsent } from "../utils/practitionerConsent";
// The "who is my practitioner right now" rule, shared with the Journal
// tab's share affordance so the two surfaces can't disagree about it.
import { resolveActivePractitioner } from "../hooks/useActivePractitionerLink";
import SharedEntriesModal from "./SharedEntriesModal";

// "From your practitioner" — the client-side face of a practitioner
// protocol. NOT the Protocol tab (that word already means the daily
// rituals — Morning Anchor etc.); this card is labeled by the
// relationship so the two never collide.
//
// Posture: additive, never disruptive. Renders ONLY when there is a
// consented active protocol (the behavior list) or a pending consent
// ask (a quiet one-line card whose tap opens the consent dialog —
// same portaled-overlay pattern as PushPrePrompt). Otherwise it is
// invisible and Home is exactly what it was.
//
// Accountability lives in the relationship, not the app: no streaks,
// no red, no counters — a behavior is a calm circle you can fill for
// today, and that's the whole interaction. Copy stays in
// Reflect/Contextualize register (rubric-guarded in tests): the card
// shows what was set and where it stands, never "you should".

export default function ProtocolCard() {
  const { token } = useAuth();
  const [pendingLink, setPendingLink] = useState(null);
  const [protocol, setProtocol] = useState(null);   // active + consented only
  const [doneToday, setDoneToday] = useState({});   // behavior_id → bool
  const [consentOpen, setConsentOpen] = useState(false);
  const [entriesOpen, setEntriesOpen] = useState(false);
  // How many written entries this client has opened to this
  // practitioner. 0 is the default and the honest resting state —
  // the footer line below says so in as many words.
  const [sharedCount, setSharedCount] = useState(0);

  useEffect(() => {
    if (!token) return;
    let dead = false;
    (async () => {
      try {
        const [linksRes, protoRes] = await Promise.all([
          practitionerLinkApi.myLinks(token),
          practitionerLinkApi.assigned(token),
        ]);
        if (dead) return;
        const links = linksRes?.links || [];
        const protocols = protoRes?.protocols || [];
        const { protocol: active, link: activeLink } =
          resolveActivePractitioner(links, protocols);
        const pending = links.find((l) => !l.consent_given) || null;
        setPendingLink(pending);
        // Flow 1 (invite redemption) and Flow 3 (practitioner signup
        // code) both set this flag right before landing here — it's
        // the one thing that turns the quiet tap-to-review card into
        // an immediately-open ask, reusing this same dialog either way.
        if (pending && consumePendingConsent()) setConsentOpen(true);
        setProtocol(active ? { ...active, practitioner: activeLink } : null);
        if (activeLink) {
          try {
            const receipt = await practitionerLinkApi.sharedEntries(activeLink.id, token);
            if (!dead) setSharedCount((receipt?.shared || []).length);
          } catch { /* receipt is additive — card works without it */ }
        }
        if (active) {
          try {
            const { adherence } = await practitionerLinkApi.adherence(active.id, token);
            if (dead) return;
            const today = localDateKey();
            const map = {};
            for (const row of adherence) {
              if (row.date === today) map[row.behavior_id] = row.completed;
            }
            setDoneToday(map);
          } catch { /* no adherence yet */ }
        }
      } catch { /* endpoint unreachable → card stays invisible */ }
    })();
    return () => { dead = true; };
  }, [token]);

  const respond = async (accept) => {
    if (!pendingLink) return;
    try {
      await practitionerLinkApi.respond(pendingLink.id, accept, token);
    } catch { /* leave the ask for next visit */ }
    setConsentOpen(false);
    setPendingLink(null);
    if (accept) {
      // A protocol may have been staged awaiting consent — refetch once.
      try {
        const { protocols } = await practitionerLinkApi.assigned(token);
        const active = protocols.find((p) => p.status === "active");
        if (active) setProtocol({ ...active, practitioner: pendingLink });
      } catch { /* next visit */ }
    }
  };

  const toggleBehavior = async (behaviorId) => {
    if (!protocol) return;
    const next = !doneToday[behaviorId];
    setDoneToday((m) => ({ ...m, [behaviorId]: next })); // optimistic, calm
    try {
      await practitionerLinkApi.logAdherence({
        protocol_id: protocol.id,
        behavior_id: behaviorId,
        date: localDateKey(),
        completed: next,
        completion_source: "self_report",
      }, token);
    } catch {
      setDoneToday((m) => ({ ...m, [behaviorId]: !next })); // roll back quietly
    }
  };

  // Nothing to show — Home unchanged.
  if (!pendingLink && !protocol) return null;

  return (
    <>
      {/* Pending consent — one quiet line, tap to review. */}
      {pendingLink && !protocol && (
        <button
          type="button"
          onClick={() => setConsentOpen(true)}
          style={{
            width: "100%", textAlign: "left", cursor: "pointer",
            background: P.surface, border: `0.5px solid ${P.border}`,
            borderRadius: 14, padding: "16px 18px",
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 600, color: P.primary, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 6 }}>
            From your practitioner
          </div>
          <div style={{ fontSize: 14, color: P.text, lineHeight: 1.5, fontWeight: 300 }}>
            {pendingLink.practitioner_name} would like to work with you in
            Haiven. <span style={{ color: P.primary }}>Review the request →</span>
          </div>
        </button>
      )}

      {/* Active, consented protocol. */}
      {protocol && (
        <div style={{
          background: P.surface, border: `0.5px solid ${P.border}`,
          borderRadius: 14, padding: "18px 18px 14px",
        }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: P.primary, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 8 }}>
            From your practitioner
          </div>
          <div style={{ fontSize: 16, color: P.text, fontWeight: 500, marginBottom: 2 }}>
            {protocol.title}
          </div>
          <div style={{ fontSize: 12, color: P.textMute, marginBottom: 14 }}>
            Set with {protocol.practitioner?.practitioner_name || "your practitioner"}
            {protocol.note ? ` — “${protocol.note}”` : ""}
          </div>

          {(protocol.behaviors || []).map((b) => {
            const done = !!doneToday[b.behavior_id];
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => toggleBehavior(b.behavior_id)}
                aria-pressed={done}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 12,
                  background: "none", border: "none", cursor: "pointer",
                  padding: "10px 0", borderTop: `1px solid ${P.border}`,
                  textAlign: "left",
                }}
              >
                <span aria-hidden="true" style={{
                  width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                  border: `1.5px solid ${done ? P.primary : P.border}`,
                  background: done ? P.primary : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.2s",
                }}>
                  {done && (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={P.bg} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </span>
                <span style={{ flex: 1 }}>
                  <span style={{ fontSize: 14, color: P.text, fontWeight: done ? 400 : 300 }}>
                    {b.behavior_name}
                    {b.target_value && <span style={{ color: P.textMid }}> · {b.target_value}</span>}
                  </span>
                  <span style={{ display: "block", fontSize: 11.5, color: P.textMute, marginTop: 1 }}>
                    {b.frequency}
                  </span>
                </span>
              </button>
            );
          })}

          {/* This line is load-bearing, so it tracks reality: it may
              only claim the journal is unshared while no entry has been
              opened to this practitioner. */}
          <button
            type="button"
            onClick={() => setEntriesOpen(true)}
            style={{
              width: "100%", textAlign: "left", background: "none",
              border: "none", cursor: "pointer",
              fontSize: 11, color: P.textMute, padding: "10px 0 0",
              borderTop: `1px solid ${P.border}`, lineHeight: 1.6,
            }}
          >
            {sharedCount === 0
              ? "Completion and signal trends are shared — none of your written entries."
              : sharedCount === 1
                ? "Completion and signal trends, plus 1 written entry you chose."
                : `Completion and signal trends, plus ${sharedCount} written entries you chose.`}
            <span style={{ color: P.primary }}> Review →</span>
          </button>
        </div>
      )}

      {consentOpen && pendingLink && (
        <ConsentDialog link={pendingLink} onRespond={respond} onClose={() => setConsentOpen(false)} />
      )}

      {entriesOpen && protocol?.practitioner && (
        <SharedEntriesModal
          link={protocol.practitioner}
          onClose={() => setEntriesOpen(false)}
          onChange={async () => {
            try {
              const receipt = await practitionerLinkApi.sharedEntries(
                protocol.practitioner.id, token);
              setSharedCount((receipt?.shared || []).length);
            } catch { /* count refreshes on next mount */ }
          }}
        />
      )}
    </>
  );
}

// Portaled full-screen consent moment — same pattern as PushPrePrompt
// (and portaled for the same reason: animated ancestors hijack
// position:fixed).
export function ConsentDialog({ link, onRespond, onClose }) {
  const [busy, setBusy] = useState(false);
  const decide = async (accept) => {
    if (busy) return;
    setBusy(true);
    await onRespond(accept);
  };
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Practitioner request"
      style={{
        position: "fixed", inset: 0, zIndex: 1000, background: P.bg,
        display: "flex", flexDirection: "column", justifyContent: "center",
        padding: "0 28px", maxWidth: 420, margin: "0 auto",
      }}
    >
      <div style={{ fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: P.primary, fontWeight: 600, marginBottom: 16 }}>
        From your practitioner
      </div>
      <h1 style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: 30, fontStyle: "italic", fontWeight: 400,
        color: P.text, lineHeight: 1.25, marginBottom: 14,
      }}>
        {link.practitioner_name} wants to share a focus protocol and see your progress.
      </h1>
      <p style={{ fontSize: 14.5, color: P.textMid, fontWeight: 300, lineHeight: 1.7, marginBottom: 8, maxWidth: 330 }}>
        Only completion data and signal trends. Nothing you've written is
        shared — later you can choose specific entries if you want to.
      </p>
      <p style={{ fontSize: 14.5, color: P.textMid, fontWeight: 300, lineHeight: 1.7, marginBottom: 34, maxWidth: 330 }}>
        You can change your mind anytime; declining or revoking pauses
        anything they've set.
      </p>
      <button
        type="button"
        onClick={() => decide(true)}
        disabled={busy}
        style={{
          width: "100%", padding: 17, background: P.dark, border: "none",
          borderRadius: 16, cursor: "pointer",
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: 18, fontStyle: "italic", color: P.bg,
          opacity: busy ? 0.6 : 1,
        }}
      >
        Share my progress
      </button>
      <button
        type="button"
        onClick={() => decide(false)}
        disabled={busy}
        style={{
          background: "none", border: "none", marginTop: 16,
          color: P.textMute, fontSize: 13.5, cursor: "pointer", padding: 10,
        }}
      >
        Decline
      </button>
      <button
        type="button"
        onClick={onClose}
        style={{
          background: "none", border: "none", marginTop: 2,
          color: P.textFaint, fontSize: 12.5, cursor: "pointer", padding: 8,
        }}
      >
        Decide later
      </button>
    </div>,
    document.body
  );
}
